import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './auth-context';
import { getUserId } from '../core/auth/token';
import { WebSocketContext } from './WebSocketContext';
import type { WsInboundMessage } from '../modules/call/types/call.types';

const WS_BASE_URL = import.meta.env.VITE_WS_URL as string;
const MAX_BACKOFF_MS = 30_000;

export const WebSocketProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef<number>(1000);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] =
    useState<WsInboundMessage | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Provider unmounted');
        wsRef.current = null;
      }
    };
  }, []);

  const connect = useCallback(function connectFn() {
    if (!WS_BASE_URL) return;

    const clientId = getUserId() ?? (user?.id ?? 'dev-web');
    const url =
      `${WS_BASE_URL}?clientId=${clientId}&deviceType=web`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) return;
        backoffRef.current = 1000;
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const msg = JSON.parse(event.data) as WsInboundMessage;
          setLastMessage(msg);
        } catch {
          // ignore non-JSON frames
        }
      };

      ws.onerror = () => {
        // errors always precede onclose; handle in onclose
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;
        setIsConnected(false);

        if (event.code === 1000) return;

        const delay = backoffRef.current;
        backoffRef.current = Math.min(delay * 2, MAX_BACKOFF_MS);
        retryTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) connectFn();
        }, delay);
      };
    } catch (err) {
      console.warn('WebSocket connect failed:', err);
    }
  }, [user]);

  const disconnect = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logged out');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    backoffRef.current = 1000;
    connect();
  }, [connect, disconnect]);

  const sendMessage = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
      return true;
    }
    console.warn('WebSocket not open; message dropped:', msg);
    return false;
  }, []);

  useEffect(() => {
    const handleOffline = () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Network offline');
      }
      setIsConnected(false);
    };

    const handleOnline = () => {
      if (user) {
        reconnect();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [reconnect, user]);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect, user]);

  return (
    <WebSocketContext.Provider
      value={{ isConnected, sendMessage, lastMessage, reconnect }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

